import { useCallback } from "react";

import {
  type ErrorCode,
  type MessageLang,
  type Messages,
  apiErrors,
} from "@kex-resources/api-errors";
import IntlMessageFormat from "intl-messageformat";
import { useIntl } from "react-intl";

const isMessageLocale = <T extends Messages>(
  value: unknown,
  messages: T,
): value is MessageLang => {
  if (typeof value !== "string") {
    return false;
  }

  return Object.keys(messages).includes(value);
};

const makeErrorMessageGetter =
  <T extends Messages>(messages: T) =>
  (locale: string) => {
    if (isMessageLocale(locale, messages)) {
      return messages[locale];
    }
    return messages.defaultMessage;
  };

export const useFormatApiErrorMessage = () => {
  const { locale } = useIntl();
  const lang = new Intl.Locale(locale).language;

  const formatApiErrorMessage = useCallback(
    <E extends ErrorCode, P extends Record<string, unknown>>(
      code: E,
      payload?: P,
    ) => {
      try {
        const error = apiErrors[code];
        const getMessage = makeErrorMessageGetter(error.messages);
        const errorMessage = getMessage(lang);
        const message = new IntlMessageFormat(errorMessage, locale);
        return message.format(payload) as string;
      } catch (error) {
        console.error(error);
        return code;
      }
    },
    [locale, lang],
  );
  return { formatApiErrorMessage };
};
